import dayjs from 'dayjs';
import { CirclePlus, FolderOpen, Lightbulb, ListChecks, Megaphone, Radio, Search } from 'lucide-react';

import { NavigationUserInfo } from '@/entities/user/components/index';
import { FreeTrialCard } from '@/features/setting/ui/free-trial-card';
import { NewSynclyLogo } from '@/shared/components/new-syncly-logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/shared/components/shadcn/sidebar';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { SavedQueriesMenuItem } from '.';
import { CollapsedMenuItem, MenuItem } from './sidebar.item';

interface Props {
  workspaceId: 'internal' | string;
}
export function WorkspaceSidebar({ workspaceId }: Props) {
  const { state } = useSidebar();
  const { workspace } = useWorkspace();
  const isCollapsed = state === 'collapsed';
  const influencerDiscoveryEnabled = useFeatureFlag('influencer-discovery');

  if (!isCollapsed)
    return (
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="flex items-stretch justify-between gap-x-1">
            <SidebarMenu>
              <SidebarMenuItem>
                <div className="flex items-center gap-2 p-2">
                  <NewSynclyLogo className="size-8!" />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {workspace?.name || 'Workspace name'}
                    </span>
                  </div>
                </div>
              </SidebarMenuItem>
            </SidebarMenu>
            <SidebarTrigger className="size-auto" />
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>SOCIAL LISTENING</SidebarGroupLabel>
            <SidebarMenu>
              <MenuItem
                route="/workspace/$workspaceId/create-new"
                name="Create new"
                Icon={CirclePlus}
              />
              <SavedQueriesMenuItem />
              <MenuItem route="/workspace/$workspaceId/boards" name="Boards" Icon={FolderOpen} />
            </SidebarMenu>
          </SidebarGroup>
          {influencerDiscoveryEnabled && (
            <SidebarGroup>
              <SidebarGroupLabel>INFLUENCER DISCOVERY</SidebarGroupLabel>
              <SidebarMenu>
                <MenuItem
                  route="/workspace/$workspaceId/campaign-planning/"
                  name="Planning"
                  Icon={Lightbulb}
                />
                <MenuItem route="/workspace/$workspaceId/discovery" name="Discover" Icon={Search} />
                <MenuItem
                  route="/workspace/$workspaceId/saved-profiles/"
                  name="Manage"
                  Icon={ListChecks}
                />
                <MenuItem
                  route="/workspace/$workspaceId/campaigns/"
                  name="Campaign"
                  Icon={Megaphone}
                />
              </SidebarMenu>
            </SidebarGroup>
          )}
        </SidebarContent>
        {workspace && (
          <SidebarFooter>
            <NavigationUserInfo
              isCollapsed={false}
              plan={workspace.plan}
              workspaceId={workspaceId}
            />
            {workspace?.plan === 'trial' && (
              <FreeTrialCard expiredAt={dayjs.unix(workspace.plan_expired_at)} />
            )}
          </SidebarFooter>
        )}
      </Sidebar>
    );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <SidebarTrigger className="size-auto" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          <SidebarMenuItem>
            <NewSynclyLogo className="mt-2 size-8" />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <CollapsedMenuItem
              route="/workspace/$workspaceId/create-new"
              name="Discover"
              Icon={CirclePlus}
            />
            <CollapsedMenuItem
              route="/workspace/$workspaceId/listening"
              name="Listening"
              Icon={Radio}
            />
            <CollapsedMenuItem
              route="/workspace/$workspaceId/boards"
              name="Boards"
              Icon={FolderOpen}
            />
            {influencerDiscoveryEnabled && (
              <>
                <CollapsedMenuItem
                  route="/workspace/$workspaceId/campaign-planning/"
                  name="Planning"
                  Icon={Lightbulb}
                />
                <CollapsedMenuItem
                  route="/workspace/$workspaceId/discovery"
                  name="Discover"
                  Icon={Search}
                />
                <CollapsedMenuItem
                  route="/workspace/$workspaceId/saved-profiles"
                  name="Manage"
                  Icon={ListChecks}
                />
                <CollapsedMenuItem
                  route="/workspace/$workspaceId/campaigns/"
                  name="Campaign"
                  Icon={Megaphone}
                />
              </>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      {workspace && (
        <SidebarFooter>
          <NavigationUserInfo isCollapsed={true} plan={workspace.plan} workspaceId={workspaceId} />
        </SidebarFooter>
      )}
    </Sidebar>
  );
}
